import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { CallIcon, UserIcon } from '@hugeicons/core-free-icons'

import { useUpdateMyProfile } from '@/features/users/services/users.convex'
import { FieldInfo } from '@/components/common/field-info'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores/authStore'
import { fullNameSchema } from '@/utils/validation'

interface SettingsFormUser {
  id: string
  name: string | null
  phone: string | null
  avatar: string | null
}

interface SettingsFormProps {
  user: SettingsFormUser
}

export function SettingsForm({ user }: SettingsFormProps) {
  const updateProfileMutation = useUpdateMyProfile()
  // Use auth profile as fallback for avatar if form field is left blank
  const authUser = useAuthStore((s) => s.user)

  const form = useForm({
    defaultValues: {
      name: user.name || '',
      phone: user.phone || '',
      avatar: user.avatar || '',
    },
    onSubmit: async ({ value }) => {
      try {
        const savedImage = value.avatar || authUser?.image || undefined
        await updateProfileMutation.mutateAsync({
          name: value.name || undefined,
          phone: value.phone || undefined,
          image: savedImage,
        })
        // Sync form fields to the saved values so they reflect the updated state
        form.setFieldValue('name', value.name)
        form.setFieldValue('phone', value.phone)
        form.setFieldValue('avatar', savedImage ?? '')
        toast.success('Profile updated successfully')
      } catch {
        // Global error handler shows API-specific toast
      }
    },
  })

  return (
    <div className="p-6 bg-muted/30 rounded-lg space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-1">Account Settings</h2>
        <p className="text-sm text-muted-foreground">
          Update your personal information and preferences
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-6"
      >
        <form.Field
          name="name"
          validators={{
            onDynamic: fullNameSchema,
            onSubmit: fullNameSchema,
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <HugeiconsIcon
                  icon={UserIcon}
                  className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"
                />
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  className="pl-9 bg-background"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
              <FieldInfo field={field} />
            </div>
          )}
        </form.Field>

        <form.Field name="phone">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <HugeiconsIcon
                  icon={CallIcon}
                  className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"
                />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  className="pl-9 bg-background"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {field.state.meta.errors[0]}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="avatar">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="avatar">Avatar URL</Label>
              <Input
                id="avatar"
                type="url"
                placeholder="Enter avatar image URL"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="bg-background"
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {field.state.meta.errors[0]}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <div className="flex justify-between items-center pt-2">
          {/* <Button
            type="button"
            variant="outline"
            onClick={() => {
              toast.info('Password change coming soon')
            }}
          >
            Change Password
          </Button> */}
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  )
}
